import { StyledTable } from '@/Components/Elements/StyledComponents/Tables'
import * as React from 'react'
import SelectRC, { Option } from 'rc-select/lib/index.js'
import {
    Box,
    FormControl,
    Grid,
    MenuItem,
    Paper,
    Select,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    TextField,
    Typography,
} from '@mui/material'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faCaretDown, faSearch } from '@fortawesome/pro-duotone-svg-icons'
import useCompany from '../../util/useCompany'
import Pagination from '../Elements/Pagination'
import LoadingDefault from '../Elements/Loading/LoadingDefault'
import AppDialog from '@/Components/AppDialog/AppDialog'
import { FormInputLabel } from '@/Components/Elements/StyledComponents/Input'
import { StyledButton } from '@/Components/Elements/StyledComponents/Buttons'

interface CompanySearchFieldProps {
    changeValue: (itemId: any, value: any, item: any) => void
    conditions: any[]
    custom_layout?: any
    embedded?: any
    errors: any | null
    isNewDossier: boolean
    item: {
        id: number
        default_locale: string
        translations: any[]
        display_position_id: number
        locked: boolean
        def_dossier_id: number
        settings: any
    }
    kedo: any
    linkId?: any
    mode: 'edit' | 'view'
    onKeyPress?: ((event: React.KeyboardEvent) => void) | null
    recheckConditions: () => void
    shoudListenForConditions: boolean
    type: string
    value?: any
}

const CompanySearchField = React.forwardRef<any, CompanySearchFieldProps>(
    (props, ref) => {
        const [fieldValue, setFieldValue] = React.useState(props.value)
        const [showAdvancedSearch, setShowAdvancedSearch] =
            React.useState(false)
        const [advancedSearchData, setAdvancedSearchData] = React.useState({
            naam: '',
            kvkNummer: '',
            huisnummer: '',
            straatnaam: '',
            plaats: '',
            vestigingsnummer: '',
            postcode: '',
        })
        const [searchResults, setSearchResults] = React.useState<any[]>([])
        const [isSearching, setIsSearching] = React.useState(false)
        const innerRef = React.useRef<any>(null)
        const {
            companies,
            pagination,
            autocomplete,
            searchCompanies,
            goToPage,
            changePageSize,
            resetPagination,
        } = useCompany()

        React.useImperativeHandle(ref, () => ({
            getValue: () => fieldValue,
            setVal: (name, value) => setFieldValue(value),
        }))

        const onSelect = (itemSelected) => {
            const [idx, value] = itemSelected.split('-')

            setFieldValue(value)
            setTimeout(() => {
                const company = companies[idx] || null
                props?.changeValue(props.item.id, company, props.item)
            }, 100)
        }

        const onClear = () => {
            setFieldValue(null)
            setTimeout(() => {
                props?.changeValue(props.item.id, null, props.item)
            }, 100)
        }

        const onSearch = (searchValue: string) => {
            if (!props.item?.settings?.company_key) {
                return
            }

            const settings: Record<string, any> = {
                kvkNummer: searchValue,
            }

            if (Object.keys(settings).length > 0) {
                autocomplete({
                    kedo: props.kedo,
                    settings: settings,
                })
            }
        }

        const onChange = (value) => {
            setFieldValue(value)
        }

        const handleAdvancedSearch = () => {
            setShowAdvancedSearch(true)
        }

        const handleCloseAdvancedSearch = () => {
            setShowAdvancedSearch(false)
            setAdvancedSearchData({
                naam: '',
                kvkNummer: '',
                huisnummer: '',
                straatnaam: '',
                plaats: '',
                vestigingsnummer: '',
                postcode: '',
            })
        }

        const handleAdvancedSearchSubmit = async () => {
            setIsSearching(true)
            resetPagination()

            const searchCriteria: Record<string, any> = {}

            if (advancedSearchData.naam)
                searchCriteria.naam = advancedSearchData.naam
            if (advancedSearchData.kvkNummer)
                searchCriteria.kvkNummer = advancedSearchData.kvkNummer
            if (advancedSearchData.huisnummer)
                searchCriteria.huisnummer = advancedSearchData.huisnummer
            if (advancedSearchData.straatnaam)
                searchCriteria.straatnaam = advancedSearchData.straatnaam
            if (advancedSearchData.plaats)
                searchCriteria.plaats = advancedSearchData.plaats
            if (advancedSearchData.vestigingsnummer)
                searchCriteria.vestigingsnummer =
                    advancedSearchData.vestigingsnummer
            if (advancedSearchData.postcode)
                searchCriteria.postcode = advancedSearchData.postcode

            if (Object.keys(searchCriteria).length > 0) {
                try {
                    const result = await searchCompanies({
                        kedo: props.kedo,
                        settings: searchCriteria,
                        page: 1,
                        resultatenPerPagina: pagination.resultatenPerPagina,
                    })

                    setSearchResults(result.companies)
                } catch (error) {
                    console.error('Search error:', error)
                    setSearchResults([])
                }
            }
            setIsSearching(false)
        }

        const handleCompanySelect = (company: any) => {
            const companyValue = `${company.kvkNummer}-${company.naam}-${company.vestigingsnummer}`
            setFieldValue(companyValue)
            setTimeout(() => {
                props?.changeValue(props.item.id, company, props.item)
            }, 100)
            handleCloseAdvancedSearch()
        }

        const handleAdvancedSearchInputChange = (
            field: string,
            value: string
        ) => {
            setAdvancedSearchData((prev) => ({
                ...prev,
                [field]: value,
            }))
        }

        const handlePageChange = async (page: number) => {
            const searchCriteria: Record<string, any> = {}

            if (advancedSearchData.naam)
                searchCriteria.naam = advancedSearchData.naam
            if (advancedSearchData.kvkNummer)
                searchCriteria.kvkNummer = advancedSearchData.kvkNummer
            if (advancedSearchData.huisnummer)
                searchCriteria.huisnummer = advancedSearchData.huisnummer
            if (advancedSearchData.straatnaam)
                searchCriteria.straatnaam = advancedSearchData.straatnaam
            if (advancedSearchData.plaats)
                searchCriteria.plaats = advancedSearchData.plaats
            if (advancedSearchData.vestigingsnummer)
                searchCriteria.vestigingsnummer =
                    advancedSearchData.vestigingsnummer
            if (advancedSearchData.postcode)
                searchCriteria.postcode = advancedSearchData.postcode

            if (Object.keys(searchCriteria).length > 0) {
                const result = await goToPage(page, props.kedo, searchCriteria)
                setSearchResults(result.companies)
            }
        }

        const handleResultatenPerPaginaChange = async (newPerPage: number) => {
            const searchCriteria: Record<string, any> = {}

            if (advancedSearchData.naam)
                searchCriteria.naam = advancedSearchData.naam
            if (advancedSearchData.kvkNummer)
                searchCriteria.kvkNummer = advancedSearchData.kvkNummer
            if (advancedSearchData.huisnummer)
                searchCriteria.huisnummer = advancedSearchData.huisnummer
            if (advancedSearchData.straatnaam)
                searchCriteria.straatnaam = advancedSearchData.straatnaam
            if (advancedSearchData.plaats)
                searchCriteria.plaats = advancedSearchData.plaats
            if (advancedSearchData.vestigingsnummer)
                searchCriteria.vestigingsnummer =
                    advancedSearchData.vestigingsnummer

            if (Object.keys(searchCriteria).length > 0) {
                const result = await changePageSize(
                    newPerPage,
                    props.kedo,
                    searchCriteria
                )
                setSearchResults(result.companies)
            }
        }

        return (
            <>
                <Grid container>
                    <Grid size={{ md: 10 }} sx={{ pl: 2 }}>
                        <SelectRC
                            ref={innerRef}
                            className="w-100 custom-select-react"
                            allowClear
                            placeholder={props.kedo.t('Search')}
                            value={fieldValue}
                            mode="combobox"
                            onChange={onChange}
                            onSearch={onSearch}
                            onSelect={onSelect}
                            onClear={onClear}
                            filterOption={false}
                            suffixIcon={
                                <FontAwesomeIcon size="xl" icon={faCaretDown} />
                            }
                            notFoundContent={props.kedo.t('No items found')}
                        >
                            {companies.map((company, idx) => (
                                <Option
                                    key={`${idx}-${company.kvkNummer}`}
                                    value={`${idx}-${company.kvkNummer}`}
                                    label={company.kvkNummer}
                                >
                                    {`${idx}-${company.kvkNummer} - ${company.naam} - ${company.vestigingsnummer}`}
                                </Option>
                            ))}
                        </SelectRC>
                    </Grid>
                    <Grid
                        size={{ md: 2 }}
                        sx={{ display: 'flex', alignItems: 'center', pl: 2 }}
                    >
                        <StyledButton
                            variant="outlined"
                            invert
                            dense
                            onClick={handleAdvancedSearch}
                            sx={{
                                display: 'flex',
                                alignItems: 'center',
                                '& svg': {
                                    mr: 0.5,
                                },
                            }}
                        >
                            <FontAwesomeIcon icon={faSearch} />
                            {props.kedo.t('Advanced')}
                        </StyledButton>
                    </Grid>
                </Grid>

                <AppDialog
                    aria-labelledby="contained-modal-title-vcenter"
                    open={showAdvancedSearch}
                    maxWidth={'lg'}
                    fullWidth
                    title={props.kedo.t('Advanced search')}
                    onClose={handleCloseAdvancedSearch}
                    headerClose={handleCloseAdvancedSearch}
                    contentProps={{ dividers: true }}
                    actions={
                        <>
                            <StyledButton
                                color="secondary"
                                onClick={handleCloseAdvancedSearch}
                            >
                                {props.kedo.t('Cancel')}
                            </StyledButton>
                            <StyledButton
                                onClick={handleAdvancedSearchSubmit}
                                disabled={isSearching || pagination.isLoading}
                            >
                                {isSearching
                                    ? props.kedo.t('Searching...')
                                    : props.kedo.t('Search')}
                            </StyledButton>
                        </>
                    }
                >
                    <Box>
                        <Box component="form" sx={{ mb: 1 }}>
                            <FormControl fullWidth margin="normal">
                                <FormInputLabel>
                                    {props.kedo.t('company.KVK_Number')}
                                </FormInputLabel>

                                <TextField
                                    fullWidth
                                    size="small"
                                    type="text"
                                    value={advancedSearchData.kvkNummer}
                                    onChange={(e) =>
                                        handleAdvancedSearchInputChange(
                                            'kvkNummer',
                                            e.target.value
                                        )
                                    }
                                    placeholder={
                                        props.kedo.t('company.Enter') +
                                        ' ' +
                                        props.kedo.t('company.KVK_Number')
                                    }
                                />
                            </FormControl>
                            <Grid container>
                                <Grid size={{ md: 6 }}>
                                    <FormControl fullWidth margin="normal">
                                        <FormInputLabel>
                                            {props.kedo.t(
                                                'company.Company_Name'
                                            )}
                                        </FormInputLabel>
                                        <TextField
                                            fullWidth
                                            size="small"
                                            type="text"
                                            value={advancedSearchData.naam}
                                            onChange={(e) =>
                                                handleAdvancedSearchInputChange(
                                                    'naam',
                                                    e.target.value
                                                )
                                            }
                                            placeholder={
                                                props.kedo.t('company.Enter') +
                                                ' ' +
                                                props.kedo.t(
                                                    'company.Company_Name'
                                                )
                                            }
                                        />
                                    </FormControl>
                                </Grid>
                                <Grid size={{ md: 6 }}>
                                    <FormControl fullWidth margin="normal">
                                        <FormInputLabel>
                                            {props.kedo.t(
                                                'company.Establishment_Number'
                                            )}
                                        </FormInputLabel>
                                        <TextField
                                            fullWidth
                                            size="small"
                                            type="text"
                                            value={
                                                advancedSearchData.vestigingsnummer
                                            }
                                            onChange={(e) =>
                                                handleAdvancedSearchInputChange(
                                                    'vestigingsnummer',
                                                    e.target.value
                                                )
                                            }
                                            placeholder={
                                                props.kedo.t('company.Enter') +
                                                ' ' +
                                                props.kedo.t(
                                                    'company.Establishment_Number'
                                                )
                                            }
                                        />
                                    </FormControl>
                                </Grid>
                            </Grid>

                            <Grid container>
                                <Grid size={{ md: 4 }}>
                                    <FormControl fullWidth margin="normal">
                                        <FormInputLabel>
                                            {props.kedo.t(
                                                'company.Street_Name'
                                            )}
                                        </FormInputLabel>
                                        <TextField
                                            fullWidth
                                            size="small"
                                            type="text"
                                            value={
                                                advancedSearchData.straatnaam
                                            }
                                            onChange={(e) =>
                                                handleAdvancedSearchInputChange(
                                                    'straatnaam',
                                                    e.target.value
                                                )
                                            }
                                            placeholder={
                                                props.kedo.t('company.Enter') +
                                                ' ' +
                                                props.kedo.t(
                                                    'company.Street_Name'
                                                )
                                            }
                                        />
                                    </FormControl>
                                </Grid>
                                <Grid size={{ md: 4 }}>
                                    <FormControl fullWidth margin="normal">
                                        <FormInputLabel>
                                            {props.kedo.t('company.Postcode')}
                                        </FormInputLabel>
                                        <TextField
                                            fullWidth
                                            size="small"
                                            type="text"
                                            value={advancedSearchData.postcode}
                                            onChange={(e) =>
                                                handleAdvancedSearchInputChange(
                                                    'postcode',
                                                    e.target.value
                                                )
                                            }
                                            placeholder={
                                                props.kedo.t('company.Enter') +
                                                ' ' +
                                                props.kedo.t('company.Postcode')
                                            }
                                        />
                                    </FormControl>
                                </Grid>
                                <Grid size={{ md: 4 }}>
                                    <FormControl fullWidth margin="normal">
                                        <FormInputLabel>
                                            {props.kedo.t(
                                                'company.House_Number'
                                            )}
                                        </FormInputLabel>
                                        <TextField
                                            fullWidth
                                            size="small"
                                            type="text"
                                            value={
                                                advancedSearchData.huisnummer
                                            }
                                            onChange={(e) =>
                                                handleAdvancedSearchInputChange(
                                                    'huisnummer',
                                                    e.target.value
                                                )
                                            }
                                            placeholder={
                                                props.kedo.t('company.Enter') +
                                                ' ' +
                                                props.kedo.t(
                                                    'company.House_Number'
                                                )
                                            }
                                        />
                                    </FormControl>
                                </Grid>
                            </Grid>

                            <Grid container>
                                <Grid size={{ md: 6 }}>
                                    <FormControl fullWidth margin="normal">
                                        <FormInputLabel>
                                            {props.kedo.t('company.City')}
                                        </FormInputLabel>
                                        <TextField
                                            fullWidth
                                            size="small"
                                            type="text"
                                            value={advancedSearchData.plaats}
                                            onChange={(e) =>
                                                handleAdvancedSearchInputChange(
                                                    'plaats',
                                                    e.target.value
                                                )
                                            }
                                            placeholder={
                                                props.kedo.t('company.Enter') +
                                                ' ' +
                                                props.kedo.t('company.City')
                                            }
                                        />
                                    </FormControl>
                                </Grid>
                            </Grid>
                        </Box>

                        {(isSearching || pagination.isLoading) && (
                            <Box
                                sx={{
                                    display: 'flex',
                                    justifyContent: 'center',
                                    alignItems: 'center',
                                    mb: 3,
                                }}
                            >
                                <LoadingDefault size={'xl'} />
                            </Box>
                        )}

                        {searchResults.length > 0 && (
                            <div>
                                <Box
                                    sx={{
                                        display: 'flex',
                                        justifyContent: 'space-between',
                                        alignItems: 'center',
                                        mb: 3,
                                    }}
                                >
                                    <Typography variant="h6">
                                        {props.kedo.t('Search Results')} (
                                        {pagination.totalResults}{' '}
                                        {pagination.totalResults === 1
                                            ? props.kedo.t('result')
                                            : props.kedo.t('results')}
                                        )
                                    </Typography>
                                    <Box
                                        sx={{
                                            display: 'flex',
                                            alignItems: 'center',
                                        }}
                                    >
                                        <Typography sx={{ mr: 2 }}>
                                            {props.kedo.t('Show')}:
                                        </Typography>
                                        <FormControl
                                            sx={{ width: 'auto', mr: 2 }}
                                            disabled={pagination.isLoading}
                                        >
                                            <Select
                                                size="small"
                                                value={
                                                    pagination.resultatenPerPagina
                                                }
                                                onChange={(e) =>
                                                    handleResultatenPerPaginaChange(
                                                        Number(e.target.value)
                                                    )
                                                }
                                            >
                                                <MenuItem value={10}>
                                                    10
                                                </MenuItem>
                                                <MenuItem value={25}>
                                                    25
                                                </MenuItem>
                                                <MenuItem value={50}>
                                                    50
                                                </MenuItem>
                                            </Select>
                                        </FormControl>
                                        <Typography>
                                            {props.kedo.t('per page')}
                                        </Typography>
                                    </Box>
                                </Box>

                                <Box
                                    sx={{
                                        maxHeight: '400px',
                                        overflowY: 'auto',
                                    }}
                                >
                                    <TableContainer
                                        component={Paper}
                                        style={{ maxHeight: 400 }}
                                    >
                                        <StyledTable
                                            size="small"
                                            stickyHeader
                                            striped
                                        >
                                            <TableHead>
                                                {/* <StyledTable striped bordered hover>
                                            <TableHead className="table-dark sticky-top"> */}
                                                <TableRow
                                                    sx={{
                                                        '& .MuiTableCell-head':
                                                            {
                                                                textAlign:
                                                                    'left',
                                                                fontWeight:
                                                                    'bold',
                                                            },
                                                    }}
                                                >
                                                    <TableCell>
                                                        {props.kedo.t(
                                                            'company.KVK_Number'
                                                        )}
                                                    </TableCell>
                                                    <TableCell>
                                                        {props.kedo.t(
                                                            'company.Company_Name'
                                                        )}
                                                    </TableCell>
                                                    <TableCell>
                                                        {props.kedo.t(
                                                            'company.Type'
                                                        )}
                                                    </TableCell>
                                                    <TableCell>
                                                        {props.kedo.t(
                                                            'company.Establishment_Number'
                                                        )}
                                                    </TableCell>
                                                    <TableCell>
                                                        {props.kedo.t(
                                                            'company.Full_address'
                                                        )}
                                                    </TableCell>
                                                    <TableCell>
                                                        {props.kedo.t(
                                                            'company.City'
                                                        )}
                                                    </TableCell>
                                                    <TableCell>
                                                        {props.kedo.t(
                                                            'Actions'
                                                        )}
                                                    </TableCell>
                                                </TableRow>
                                            </TableHead>
                                            <TableBody sx={{}}>
                                                {searchResults.map(
                                                    (company, index) => (
                                                        <TableRow
                                                            key={
                                                                (pagination.currentPage -
                                                                    1) *
                                                                    pagination.resultatenPerPagina +
                                                                index
                                                            }
                                                        >
                                                            <TableCell>
                                                                {
                                                                    company.kvkNummer
                                                                }
                                                            </TableCell>
                                                            <TableCell>
                                                                {company.naam}
                                                            </TableCell>
                                                            <TableCell>
                                                                {company.type ||
                                                                    'N/A'}
                                                            </TableCell>
                                                            <TableCell>
                                                                {
                                                                    company.vestigingsnummer
                                                                }
                                                            </TableCell>
                                                            <TableCell>
                                                                {company.volledigAdres ||
                                                                    `${company.straatnaam} ${company.huisnummer}, ${company.plaats}`}
                                                            </TableCell>
                                                            <TableCell>
                                                                {company.plaats}
                                                            </TableCell>
                                                            <TableCell>
                                                                <StyledButton
                                                                    variant="outlined"
                                                                    dense
                                                                    invert
                                                                    onClick={() =>
                                                                        handleCompanySelect(
                                                                            company
                                                                        )
                                                                    }
                                                                >
                                                                    {props.kedo.t(
                                                                        'Select'
                                                                    )}
                                                                </StyledButton>
                                                            </TableCell>
                                                        </TableRow>
                                                    )
                                                )}
                                            </TableBody>
                                        </StyledTable>
                                    </TableContainer>
                                </Box>

                                {pagination.totalResults >
                                    pagination.resultatenPerPagina && (
                                    <Pagination
                                        pager={{
                                            pages: pagination.totalPages,
                                            current: pagination.currentPage - 1,
                                        }}
                                        onChangePage={(pageIndex) =>
                                            handlePageChange(pageIndex + 1)
                                        }
                                    />
                                )}

                                {pagination.totalResults > 0 && (
                                    <Typography
                                        color="text.disabled"
                                        sx={{ textAlign: 'center', mt: 1 }}
                                    >
                                        {props.kedo.t('Showing')}{' '}
                                        {(pagination.currentPage - 1) *
                                            pagination.resultatenPerPagina +
                                            1}
                                        -
                                        {Math.min(
                                            pagination.currentPage *
                                                pagination.resultatenPerPagina,
                                            pagination.totalResults
                                        )}{' '}
                                        {props.kedo.t('of')}{' '}
                                        {pagination.totalResults}{' '}
                                        {props.kedo.t('results')}
                                    </Typography>
                                )}
                            </div>
                        )}

                        {searchResults.length === 0 && !isSearching && (
                            <Typography
                                color="text.disabled"
                                sx={{ textAlign: 'center' }}
                            >
                                {props.kedo.t('search_criteria')}
                            </Typography>
                        )}
                    </Box>
                </AppDialog>
            </>
        )
    }
)

CompanySearchField.displayName = 'CompanySearchField'

export default CompanySearchField
